'use client'

import { useMutation } from '@tanstack/react-query'
import { useFileUpload } from '@/lib/upload/useFileUpload'
import {
  submitPublicBuildPortfolioCase,
  type SubmitPublicBuildPortfolioCaseInput,
  type SubmitPublicCaseInput,
  type SubmitPublicCaseResult,
} from '@/features/onboarding/api/onboarding.api'

/**
 * The first unauthenticated write in the app — deliberately does not go through `useApiFetch()`
 * (which always attaches a Clerk bearer token; there is no session here). Reuses `useFileUpload`
 * directly instead: it already degrades correctly with no session (its `Authorization` header is
 * only sent when a token exists), and gives this multipart submission real upload progress.
 *
 * No `onError` toast — a failed public funnel submission surfaces inline in the form itself, the
 * same as any other validation error, rather than a transient toast that can be missed.
 */
export function useSubmitPublicCase() {
  const { upload, progress, isUploading } = useFileUpload()

  const mutation = useMutation({
    mutationFn: (input: SubmitPublicCaseInput) => {
      const formData = new FormData()
      formData.append('serviceType', input.serviceType)
      formData.append('mobile', input.mobile)
      formData.append('consultationSlotAt', input.consultationSlotAt)
      if (input.document) {
        formData.append('file', input.document.file)
        formData.append('kind', input.document.kind)
        if (input.document.password) formData.append('password', input.document.password)
      }
      return upload<SubmitPublicCaseResult>('/api/v1/public/cases', formData, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
    },
  })

  return { ...mutation, progress, isUploading }
}

/** Mirrors `useSubmitPublicCase` above — same "no `onError` toast, errors surface inline"
 * reasoning. Plain `useMutation`, not `useFileUpload`: Build's intake has no file, so there's no
 * upload progress to track either. */
export function useSubmitPublicBuildPortfolioCase() {
  return useMutation({
    mutationFn: (input: SubmitPublicBuildPortfolioCaseInput) =>
      submitPublicBuildPortfolioCase(input),
  })
}
